import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { WifiOff, Wifi } from 'lucide-react';

const NetworkContext = createContext(null);

// Check if running in Capacitor native app
const isNativePlatform = () => {
  return window.Capacitor?.isNativePlatform?.() || false;
};

export function NetworkProvider({ children }) {
  const [isOnline, setIsOnline] = useState(true);
  const [showOnlineBanner, setShowOnlineBanner] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const handleOnline = useCallback(() => {
    setIsOnline(true);
    // Show "back online" banner only if we were offline
    if (wasOffline) {
      setShowOnlineBanner(true);
      setTimeout(() => setShowOnlineBanner(false), 3000);
    }
    setWasOffline(false);
  }, [wasOffline]);

  const handleOffline = useCallback(() => {
    setIsOnline(false);
    setWasOffline(true);
  }, []);

  useEffect(() => {
    let networkListener = null;

    const setupNetworkListener = async () => {
      if (isNativePlatform()) {
        // Use Capacitor Network plugin for native apps
        try {
          const { Network } = await import('@capacitor/network');

          // Get initial status
          const status = await Network.getStatus();
          setIsOnline(status.connected);
          if (!status.connected) {
            setWasOffline(true);
          }

          // Listen for changes
          networkListener = await Network.addListener('networkStatusChange', (status) => {
            if (status.connected) {
              handleOnline();
            } else {
              handleOffline();
            }
          });
        } catch (error) {
          console.warn('Capacitor Network plugin not available, using browser API');
          // Fallback to browser API
          setIsOnline(navigator.onLine);
          window.addEventListener('online', handleOnline);
          window.addEventListener('offline', handleOffline);
        }
      } else {
        // Use browser API for web
        setIsOnline(navigator.onLine);
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
      }
      setInitialized(true);
    };

    setupNetworkListener();

    return () => {
      if (networkListener) {
        networkListener.remove();
      }
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [handleOnline, handleOffline]);

  // Don't show anything until initialized
  if (!initialized) {
    return <>{children}</>;
  }

  return (
    <NetworkContext.Provider value={{ isOnline }}>
      {/* Offline Banner */}
      {!isOnline && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 shadow-lg animate-slide-down safe-area-top">
          <div className="w-full px-4 py-2.5 flex items-center justify-center gap-2">
            <WifiOff className="w-4 h-4 text-white flex-shrink-0" />
            <span className="text-sm font-medium text-white text-center">
              You&apos;re offline - viewing saved data
            </span>
          </div>
        </div>
      )}

      {/* Back Online Banner */}
      {showOnlineBanner && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-income-500 shadow-lg animate-slide-down safe-area-top">
          <div className="w-full px-4 py-2.5 flex items-center justify-center gap-2">
            <Wifi className="w-4 h-4 text-white flex-shrink-0" />
            <span className="text-sm font-medium text-white text-center">
              Back online - syncing data
            </span>
          </div>
        </div>
      )}

      {/* Add top padding when offline banner is showing */}
      <div className={!isOnline ? 'pt-10' : ''}>
        {children}
      </div>
    </NetworkContext.Provider>
  );
}

export function useNetwork() {
  const context = useContext(NetworkContext);
  if (!context) {
    throw new Error('useNetwork must be used within a NetworkProvider');
  }
  return context;
}
